import { Injectable, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Driver, driver, DriveStep } from 'driver.js'
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import confetti from 'canvas-confetti';

/**
 * Onboarding selector constants
 */
export const ONBOARDING_SELECTORS = {
  NEXT_BUTTON: '.driver-popover-next-btn',
  PREV_BUTTON: '.driver-popover-prev-btn',
  CLOSE_BUTTON: '.driver-popover-close-btn',
  DESCRIPTION: '.driver-popover-description',
} as const;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  /**
   * driver property
   * @type {Driver}
   */
  private driver: Driver;
  /**
   * steps property
   * @type {DriveStep[]}
   */
  private steps: DriveStep[] = [];
  /**
   * A BehaviorSubject that holds the current error message related to the email or username existence check.
   */
  public errorMsg = new BehaviorSubject<string>(null);
  /**
   * A BehaviorSubject that holds the current section name in the onboarding or user journey process.
   */
  public currentSection$ = new BehaviorSubject<string>('');
  /**
   * A Subject that is used to notify when a dropdown should be closed.
   */
  public closeDropdown = new Subject<{ triggeredBy: string }>();
  /**
   * A Subject that is used to notify when an element is collapsed.
   */
  public isCollapsed = new Subject<void>();
  public driverStatus = signal<boolean>(false);

  // Email/username validation regex, compiled once.
  private readonly emailRegex = new RegExp(
    '(?=^.{5,50}$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$|qair\\.energy$|^$'
  );

  // Stable references for document event listeners so removeEventListener can match them exactly
  // across multiple startTour() calls without accumulating duplicate listeners.
  private readonly preventKeyPress = (event: KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  private readonly preventScroll = (event: Event) => {
    event.preventDefault();
  };

  private isScrollDisabled = false;

  constructor() { }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private setButtonEnabled(button: HTMLElement, enabled: boolean): void {
    button.style.pointerEvents = enabled ? 'auto' : 'none';
    button.style.opacity = enabled ? '1' : '0.5';
  }

  /**
   * Map of element selectors to their next-button validation handlers.
   * Each handler receives the next-button element and updates its enabled state.
   */
  private readonly elementValidators: Record<string, (nextButton: HTMLElement) => void> = {
    '#firstName-attribute': (btn) => {
      const input = document.querySelector('#firstName-attribute input') as HTMLInputElement;
      this.setButtonEnabled(btn, !!input?.value.trim());
    },
    '#lastName-attribute': (btn) => {
      const input = document.querySelector('#lastName-attribute input') as HTMLInputElement;
      this.setButtonEnabled(btn, (input?.value.trim().length ?? 0) >= 2);
    },
    '#email-or-username': (btn) => {
      const emailInput = document.querySelector('#email input') as HTMLInputElement;
      const usernameInput = document.querySelector('#username input') as HTMLInputElement;
      const isEmailValid = emailInput?.value.trim() && this.emailRegex.test(emailInput.value.trim());
      const isUsernameValid = (usernameInput?.value.trim().length ?? 0) >= 2;
      this.setButtonEnabled(btn, !!(isEmailValid || isUsernameValid));
      if (this.errorMsg.value) {
        const desc = document.querySelector(ONBOARDING_SELECTORS.DESCRIPTION) as HTMLElement;
        if (desc) desc.innerText = this.errorMsg.value;
      }
    },
    '#url-button': (btn) => {
      const urlInput = document.querySelector('#url-button input') as HTMLInputElement;
      const urlButton = document.querySelector(ONBOARDING_SELECTORS.NEXT_BUTTON) as HTMLButtonElement;
      if (urlInput?.value?.trim().length > 0) {
        btn.classList.remove('driver-popover-btn-disabled');
        if (urlButton) urlButton.removeAttribute('disabled');
      }
    },
    '#login': (btn) => {
      const input = document.querySelector('#login input') as HTMLInputElement;
      this.setButtonEnabled(btn, (input?.value.trim().length ?? 0) >= 2);
    },
    '#attribute-name': (btn) => {
      const input = document.querySelector('#attribute-name input') as HTMLInputElement;
      this.setButtonEnabled(btn, (input?.value.trim().length ?? 0) >= 2);
    },
  };

  /**
   * Map of controlName values to their value-based validation functions.
   * Returns true when the button should be enabled.
   */
  private readonly controlValidators: Record<string, (value: string) => boolean> = {
    applicationName: (v) => v?.trim() === '' || v.trim().length >= 2,
    login: (v) => (v?.trim().length ?? 0) >= 2,
    length: (v) => !!v && +v >= 1 && +v <= 60,
    name: (v) => (v?.trim().length ?? 0) >= 2 && v?.trim() !== 'all' && v?.trim() !== 'no_one',
  };

  // ─── Tour lifecycle ─────────────────────────────────────────────────────────

  /**
   * Initiates the guided onboarding tour using Driver.js.
   *
   * - Disables scrolling and key-based interactions to prevent user interference during the tour.
   * - Configures Driver.js with custom steps, navigation behavior, and validation logic.
   * - Enables or disables the "Next" button on certain steps based on input validation.
   * - Focuses input fields when highlighted.
   * - Re-enables scrolling and keyboard control after the tour ends.
   */
  startTour() {
    const disableScroll = () => {
      if (this.isScrollDisabled) return;
      this.isScrollDisabled = true;
      document.addEventListener('keydown', this.preventKeyPress);
      document.addEventListener('wheel', this.preventScroll, { passive: false });
      document.addEventListener('touchmove', this.preventScroll, { passive: false });
    };
    const enableScroll = () => {
      if (!this.isScrollDisabled) return;
      this.isScrollDisabled = false;
      document.removeEventListener('keydown', this.preventKeyPress);
      document.removeEventListener('wheel', this.preventScroll);
      document.removeEventListener('touchmove', this.preventScroll);
    };

    const enableElementScroll = (element: HTMLElement) => {
      ['wheel', 'touchmove'].forEach(event => {
        element.removeEventListener(event, this.preventScroll);
        element.addEventListener(event, (e) => e.stopPropagation());
      });
    };

    this.driver = driver({
      showProgress: false,
      allowClose: false,
      steps: this.steps,
      allowKeyboardControl: false,
      disableActiveInteraction: false,
      onPopoverRender: (popover, { config, state }) => {
        disableScroll();
        const nextButton = popover.nextButton;
        if (nextButton) {
          const activeElement = state.activeStep?.element as string;
          const validator = this.elementValidators[activeElement];
          if (validator) validator(nextButton);
        }
      },
      onHighlighted: (element) => {
        if (element) {
          // element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = element.querySelector(
            "input:not([formControlName='url']):not([formControlName='path']), textarea"
          ) as HTMLInputElement;
          if (input) input.focus();
          if (element.tagName.toLowerCase() === 'ul' && element.classList.contains('available-items')) {
            enableElementScroll(element as HTMLElement);
          }
        }
      },
      onDestroyed: () => {
        enableScroll();
      },
    });

    this.driver.drive();
    this.driverStatus.set(true);
  }

  // ─── Step navigation ────────────────────────────────────────────────────────

  /**
   * Retrieves the currently active step from the Driver.js tour instance.
   */
  getActiveStep(): DriveStep {
    if (this.driver) {
      return this.driver.getActiveStep();
    }
  }

  isTourActive(): boolean {
    return this.driver?.isActive() ?? false;
  }

  /**
   * Highlights a specific step in the Driver.js tour.
   */
  highlightStep(step: DriveStep) {
    this.driver.highlight(step);
  }

  /**
   * Adds new unique steps to the existing Driver.js steps array based on the step title.
   * Duplicate steps (matching popover title) are ignored.
   */
  setSteps(steps: DriveStep[]) {
    const newSteps = steps.filter(
      (newStep) =>
        !this.steps.some(
          (existingStep) => existingStep?.popover.title === newStep.popover.title
        )
    );
    this.steps.push(...newSteps);
    if (this.driver) this.driver.refresh();
  }

  /**
   * Clears all current steps from the Driver.js instance and resets the internal steps array.
   */
  clearSteps() {
    if (this.driver) {
      this.steps = [];
      this.driver.setSteps(this.steps);
    }
  }

  /**
   * Advances the Driver.js tour to the next step if the tour is currently active.
   */
  moveNextStep() {
    if (this.driver?.isActive()) {
      this.driver.moveNext();
    }
  }

  /**
   * Moves the Driver.js tour to the previous step if the tour is currently active.
   */
  movePreviousStep() {
    if (this.driver?.isActive()) {
      this.driver.movePrevious();
    }
  }

  /**
   * Destroys the current Driver.js popover tour if active.
   * Clears the current section, resets steps, destroys the instance,
   * updates the driver status, and resets email/username validation state.
   */
  destroyPopover(): void {
    if (this.driver) {
      this.setCurrentsection('');
      this.steps = [];
      this.driver.destroy();
      this.driverStatus.set(false);
      this.setEmailOrUsernameExist(null);
    }
    // Safety: always re-enable scroll regardless of driver state,
    // in case onDestroyed was never called (e.g. missing target element).
    document.removeEventListener('keydown', this.preventKeyPress);
    document.removeEventListener('wheel', this.preventScroll);
    document.removeEventListener('touchmove', this.preventScroll);
  }

  /**
   * Refreshes the Driver.js popover tour if it is currently active.
   */
  refresh() {
    if (this.driver?.isActive()) {
      this.driver.refresh();
    }
  }

  /**
   * Starts the tour at the step associated with the specified element selector.
   */
  driveToElement(elementSelector: string): void {
    const stepIndex = this.steps.findIndex((step) => step.element === elementSelector);
    if (stepIndex !== -1 && this.driver) {
      this.driver.drive(stepIndex);
    }
  }

  /**
   * Polls for a DOM element to appear, then moves to the next Driver.js step if allowed.
   * Gives up after 10 seconds to prevent orphaned intervals.
   */
  waitForElementAndMoveNext(selector: string, interval = 500, allowNext: boolean = false) {
    const maxWaitMs = 10000;
    const started = Date.now();
    const waitForElement = setInterval(() => {
      const element = document.querySelector(selector);
      const timedOut = Date.now() - started >= maxWaitMs;
      if (element || timedOut) {
        if (element && allowNext) {
          this.moveNextStep();
        }
        clearInterval(waitForElement);
      }
    }, interval);
  }

  // ─── Button state ───────────────────────────────────────────────────────────

  /**
   * Updates the state (enabled/disabled) of a popover's next button based on various control values.
   */
  updatePopoverButtonState(
    selector: string,
    control?: any,
    emailControl?: AbstractControl,
    usernameControl?: AbstractControl,
    value?: string,
    controlName?: string
  ) {
    const popover = document.querySelector('.driver-popover') as HTMLElement;
    if (!popover) return;
    const nextButton = popover.querySelector(selector) as HTMLElement;
    if (!nextButton) return;

    const enabled = this.resolveButtonEnabled(control, emailControl, usernameControl, value, controlName);
    if (enabled) {
      nextButton.style.pointerEvents = '';
      nextButton.style.opacity = '';
    } else {
      nextButton.style.pointerEvents = 'none';
      nextButton.style.opacity = '0.5';
    }
  }

  private resolveButtonEnabled(
    control: any,
    emailControl: AbstractControl,
    usernameControl: AbstractControl,
    value: string,
    controlName: string
  ): boolean {
    // Case 1: no control + no controlName → email/username validation
    if (!control && controlName === undefined) {
      const isEmailValid = emailControl?.value?.trim() && this.emailRegex.test(emailControl.value.trim());
      const isUsernameValid = (usernameControl?.value?.trim().length ?? 0) >= 2;
      return !!(isEmailValid || isUsernameValid);
    }

    // Case 2: named control validator (applicationName requires !control; login/length/name are order-dependent)
    if (!control && controlName === 'applicationName') {
      return this.controlValidators.applicationName(value);
    }
    if (controlName !== undefined) {
      const validator = this.controlValidators[controlName];
      if (validator) return validator(value);
    }

    // Case 3: generic Angular form control
    return !!(control?.valid && control.value !== '' && control.value !== undefined);
  }

  /**
   * Checks the input validation during onboarding and updates the popover button state accordingly.
   */
  checkInputValidationForOnboarding(controlName: string, showUserJourney: boolean, value?: string) {
    if (showUserJourney) {
      this.refresh();
      this.updatePopoverButtonState(ONBOARDING_SELECTORS.NEXT_BUTTON, null, null, null, value, controlName);
    }
  }

  // ─── State accessors ────────────────────────────────────────────────────────

  /**
   * Checks whether the onboarding (Driver.js) is currently active.
   */
  isOnbording() {
    return this.driver?.isActive();
  }

  /**
   * Updates the current onboarding section if it differs from the current value.
   */
  setCurrentsection(section: string) {
    if (this.currentSection$.getValue() !== section) {
      this.currentSection$.next(section);
    }
  }

  /**
   * Returns an observable of the current onboarding section.
   */
  subCurrentSection(): Observable<string> {
    return this.currentSection$.asObservable();
  }

  /**
   * Returns the current value of the onboarding section.
   */
  getCurrentSection() {
    return this.currentSection$.getValue();
  }

  /**
   * Sets the error message for the email or username existence check.
   */
  setEmailOrUsernameExist(errorMessage: string) {
    this.errorMsg.next(errorMessage);
  }

  /**
   * Gets the current error message for the email or username existence check.
   */
  subEmailOrUsernameExist(): string {
    return this.errorMsg.getValue();
  }

  // ─── Confetti ───────────────────────────────────────────────────────────────

  /**
   * Launches a confetti animation for a duration of 15 seconds.
   */
  launchConfetti() {
    const duration = 15 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  }

  // ─── Helper methods ─────────────────────────────────────────────────────────

  /**
   * Starts the tour at a specific element if it exists in the DOM.
   * @param selector - CSS selector for the element to start the tour at
   * @returns true if the element was found and tour started, false otherwise
   */
  startTourAtElement(selector: string): boolean {
    const element = document.querySelector(selector);
    if (element) {
      this.startTour();
      this.driveToElement(selector);
      return true;
    }
    return false;
  }

  /**
   * Handles onboarding-related errors by destroying the popover if needed.
   * @param response - API response object with success property
   */
  handleOnboardingError(response: { success: number }): void {
    if (response.success === -1) {
      this.destroyPopover();
    }
  }
}
